import React, { useState } from "react";

/* ── Style helpers ─────────────────────────────────── */
function iconBtn(S, active = false, danger = false) {
  return {
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "34px", minWidth: "34px", padding: "0 10px",
    borderRadius: "8px", border: `1px solid ${active ? S.accent : S.panelBorder}`,
    background: active ? S.accent : "transparent",
    color: active ? "#fff" : danger ? "#E55" : S.text,
    fontSize: "0.78rem", fontWeight: 600,
    cursor: "pointer", whiteSpace: "nowrap", gap: "5px",
    fontFamily: "'Outfit', sans-serif",
    transition: "all 0.18s ease",
    flexShrink: 0,
  };
}

function navBtn(S, disabled) {
  return {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "32px", height: "32px", borderRadius: "8px",
    border: `1px solid ${S.panelBorder}`, background: "transparent",
    color: S.text, fontSize: "1.1rem",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.3 : 1,
    transition: "all 0.15s",
    flexShrink: 0,
  };
}

/* ── Animated waveform ──────────────────────────────── */
function Waveform({ active, accent }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: "2px", height: "16px" }}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} style={{
          width: "2.5px", borderRadius: "2px", background: accent,
          height: active ? undefined : "3px",
          opacity: active ? 1 : 0.25,
          animation: active ? `waveBar${i % 3} 0.8s ease-in-out ${i * 0.12}s infinite` : "none",
          transition: "height 0.3s, opacity 0.3s",
        }} />
      ))}
    </span>
  );
}

/* ── Divider ────────────────────────────────────────── */
function Divider({ S }) {
  return <div style={{ width: "1px", height: "22px", background: S.panelBorder, opacity: 0.6, flexShrink: 0 }} />;
}

/* ══════════════════════════════════════════════════════ */
export default function ReaderToolbar({
  S, theme, book, loadingPdf, loadError,
  pageNum, numPages, showRight, rightPageNum,
  spreadMode, setSpreadMode,
  zoom, setZoom,
  changeTheme,
  showAudioPlayer, setShowAudioPlayer,
  isSpeaking, stopSpeech, startSpeech, toggleSpeech, playDisabled,
  voiceGroups, selectedVoiceName, setSelectedVoiceName, setVoiceGender,
  showNotesPanel, setShowNotesPanel, annotationCount,
  goPrev, goNext, handlePageSubmit, pageInputRef,
  navigate, stopSpeechOnNavigation,
  selectedTextInfo, saveAnnotation, openNoteComposer, clearSelectedText, handleAskScholar,
}) {
  const [showSettings, setShowSettings] = useState(false);

  const zoomIn    = () => setZoom((z) => Math.min(z + 0.15, 3.0));
  const zoomOut   = () => setZoom((z) => Math.max(z - 0.15, 0.5));
  const zoomReset = () => setZoom(1.15);

  const bestFemale = voiceGroups?.female?.find((v) =>
    ["samantha","zira","siri"].some((n) => v.name.toLowerCase().includes(n))
  ) || voiceGroups?.female?.[0] || null;

  const bestMale = voiceGroups?.male?.find((v) =>
    ["alex","david","daniel","siri"].some((n) => v.name.toLowerCase().includes(n))
  ) || voiceGroups?.male?.[0] || null;

  const femaleActive = bestFemale && selectedVoiceName === bestFemale.name;
  const maleActive   = bestMale   && selectedVoiceName === bestMale.name;
  const ttsOff       = !showAudioPlayer;
  const progress     = numPages ? Math.round((pageNum / numPages) * 100) : 0;

  return (
    <header style={{
      background: S.panelBg,
      borderBottom: `1px solid ${S.panelBorder}`,
      boxShadow: `0 2px 16px ${S.shadow}`,
      flexShrink: 0, position: "relative", zIndex: 20,
      transition: "background 0.3s, border-color 0.3s",
    }}>

      {/* ── MOBILE SELECTION OVERLAY ── */}
      {selectedTextInfo && window.innerWidth <= 768 && (
        <div style={{
          position: "absolute", inset: 0,
          background: S.panelBg, borderBottom: `1px solid ${S.panelBorder}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 0.75rem", zIndex: 40, gap: "8px",
          animation: "fadeUp 0.2s ease both",
        }}>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSelectedText}
            style={{ background: "none", border: "none", color: S.text, fontSize: "1rem", cursor: "pointer", opacity: 0.6 }}
          >✕</button>
          <span style={{ flex: 1, fontSize: "0.7rem", color: S.text, opacity: 0.75, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: "italic" }}>
            "{selectedTextInfo.text}"
          </span>
          <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
            {[
              { label: "🎓 Explain", action: () => handleAskScholar(selectedTextInfo), bg: `${S.accent}22`, border: `${S.accent}55`, color: S.accent },
              { label: "Highlight",  action: () => saveAnnotation("highlight", selectedTextInfo), bg: S.savedHighlightBg, border: S.savedHighlightRing, color: S.text },
              { label: "Note",       action: () => openNoteComposer(selectedTextInfo), bg: S.noteBg, border: S.noteRing, color: theme === "dark" ? S.noteText : "#1F4E6D" },
            ].map(({ label, action, bg, border, color }) => (
              <button key={label}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => { e.preventDefault(); action(); }}
                onClick={action}
                style={{ height: "28px", padding: "0 10px", borderRadius: "20px", border: `1px solid ${border}`, background: bg, color, fontSize: "0.68rem", fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}
              >{label}</button>
            ))}
          </div>
        </div>
      )}

      {/* ═══ MAIN TOOLBAR BAR ═══ */}
      <div style={{ height: "58px", display: "flex", alignItems: "center", padding: "0 1.25rem", gap: "10px" }}>

        {/* ── LEFT: Exit + Title ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "0 0 auto", minWidth: 0 }}>
          <button
            id="reader-exit"
            onClick={() => { stopSpeechOnNavigation?.(); navigate(`/book/${book.id}`, { replace: true }); }}
            style={{ ...iconBtn(S), padding: "0 12px", fontWeight: 700, letterSpacing: "0.02em" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = S.hover; e.currentTarget.style.borderColor = S.accent; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = S.panelBorder; }}
          >
            ← Exit
          </button>

          <Divider S={S} />

          {/* Book identity */}
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span className="tb-title" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "0.95rem", fontWeight: 700, color: S.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "clamp(80px, 18vw, 220px)", lineHeight: 1.2 }}>
              {book.title}
            </span>
            <span className="tb-author" style={{ fontSize: "0.65rem", color: S.text, opacity: 0.45, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "clamp(80px, 18vw, 220px)" }}>
              {book.author}
            </span>
          </div>
        </div>

        {/* ── CENTER: Page Nav + TTS ── */}
        <div className="reader-toolbar-desktop" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
          {!loadingPdf && !loadError && (
            <>
              {/* Page navigation pill */}
              <div style={{ display: "flex", alignItems: "center", background: S.bg, border: `1px solid ${S.panelBorder}`, borderRadius: "10px", overflow: "hidden" }}>
                <button
                  id="reader-prev" onClick={goPrev} disabled={pageNum <= 1}
                  style={{ ...navBtn(S, pageNum <= 1), border: "none", borderRadius: "0", width: "34px", height: "36px" }}
                  onMouseEnter={(e) => { if (pageNum > 1) e.currentTarget.style.background = S.hover; }}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >‹</button>

                <div style={{ width: "1px", height: "22px", background: S.panelBorder, opacity: 0.5 }} />

                <form onSubmit={handlePageSubmit} style={{ display: "flex", alignItems: "center", padding: "0 8px", gap: "4px" }}>
                  <input
                    id="reader-page-input" ref={pageInputRef}
                    type="number" defaultValue={pageNum} min={1} max={numPages}
                    style={{
                      width: "40px", background: "transparent", border: "none", outline: "none",
                      color: S.text, fontWeight: 700, fontSize: "0.85rem", textAlign: "center",
                      fontFamily: "'Outfit', sans-serif",
                    }}
                  />
                  <span style={{ fontSize: "0.72rem", color: S.text, opacity: 0.45, whiteSpace: "nowrap" }}>
                    {showRight ? `–${rightPageNum}` : ""} of {numPages}
                  </span>
                </form>

                <div style={{ width: "1px", height: "22px", background: S.panelBorder, opacity: 0.5 }} />

                <button
                  id="reader-next" onClick={goNext} disabled={pageNum >= numPages}
                  style={{ ...navBtn(S, pageNum >= numPages), border: "none", borderRadius: "0", width: "34px", height: "36px" }}
                  onMouseEnter={(e) => { if (pageNum < numPages) e.currentTarget.style.background = S.hover; }}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >›</button>
              </div>

              <Divider S={S} />

              {/* TTS capsule */}
              <div style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "5px 12px 5px 10px", borderRadius: "10px",
                border: `1.5px solid ${ttsOff ? S.panelBorder : S.accent}`,
                background: ttsOff ? "transparent" : `${S.accent}14`,
                boxShadow: ttsOff ? "none" : `0 0 0 3px ${S.accent}18`,
                opacity: ttsOff ? 0.65 : 1,
                transition: "all 0.3s ease",
              }}>
                <Waveform active={isSpeaking} accent={S.accent} />

                <span style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: ttsOff ? S.text : isSpeaking ? S.accent : S.text, opacity: ttsOff ? 0.45 : isSpeaking ? 1 : 0.6, minWidth: "42px", transition: "color 0.25s" }}>
                  {ttsOff ? "Audio" : isSpeaking ? "Reading" : "Paused"}
                </span>

                <button
                  onClick={() => { if (ttsOff) { setShowAudioPlayer(true); setTimeout(() => startSpeech(), 100); } else { toggleSpeech(); } }}
                  disabled={playDisabled}
                  style={{ width: "28px", height: "28px", borderRadius: "50%", border: "none", background: `linear-gradient(135deg,${S.accent},${S.accent}cc)`, color: "#fff", fontSize: "0.7rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 2px 8px ${S.accent}44`, transition: "transform 0.12s" }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.12)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
                >{isSpeaking ? "⏸" : "▶"}</button>

                <div style={{ display: "flex", gap: "3px" }}>
                  {[[bestFemale, femaleActive, "Female"], [bestMale, maleActive, "Male"]].map(([voice, isActive, label]) =>
                    voice ? (
                      <button key={label}
                        onClick={() => { setSelectedVoiceName(voice.name); setVoiceGender(label.toLowerCase()); if (ttsOff) setShowAudioPlayer(true); }}
                        style={{ height: "22px", padding: "0 8px", borderRadius: "20px", border: `1px solid ${isActive ? S.accent : S.panelBorder}`, background: isActive ? S.accent : "transparent", color: isActive ? "#fff" : S.text, fontSize: "0.6rem", fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "all 0.15s" }}
                      >{label}</button>
                    ) : null
                  )}
                </div>

                <button
                  onClick={() => { stopSpeech(true); setShowAudioPlayer(false); }}
                  style={{ width: "18px", height: "18px", borderRadius: "50%", border: `1px solid ${S.panelBorder}`, background: "none", color: S.text, fontSize: "0.55rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.4, transition: "all 0.15s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.background = S.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.4"; e.currentTarget.style.background = "none"; }}
                >✕</button>
              </div>
            </>
          )}
        </div>

        {/* ── RIGHT: Controls ── */}
        <div className="reader-toolbar-desktop" style={{ display: "flex", alignItems: "center", gap: "6px", flex: "0 0 auto" }}>

          {/* Notes toggle */}
          <button id="reader-notes-toggle"
            onClick={() => setShowNotesPanel((v) => !v)}
            style={iconBtn(S, showNotesPanel)}
            onMouseEnter={(e) => { if (!showNotesPanel) { e.currentTarget.style.background = S.hover; e.currentTarget.style.borderColor = S.accent; } }}
            onMouseLeave={(e) => { if (!showNotesPanel) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = S.panelBorder; } }}
          >
            ✎ <span className="tb-btn-text">Notes{annotationCount > 0 ? ` (${annotationCount})` : ""}</span>
          </button>

          {/* Read aloud toggle */}
          <button id="reader-speech-trigger"
            onClick={() => { if (showAudioPlayer) { stopSpeech(true); setShowAudioPlayer(false); } else { setShowAudioPlayer(true); if (!playDisabled) setTimeout(() => startSpeech(), 300); } }}
            style={iconBtn(S, showAudioPlayer)}
            onMouseEnter={(e) => { if (!showAudioPlayer) { e.currentTarget.style.background = S.hover; e.currentTarget.style.borderColor = S.accent; } }}
            onMouseLeave={(e) => { if (!showAudioPlayer) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = S.panelBorder; } }}
          >
            {isSpeaking ? "🔊" : "🔈"} <span className="tb-btn-text">{showAudioPlayer ? "Audio ON" : "Read Aloud"}</span>
          </button>

          {/* Spread mode */}
          <button id="reader-spread-toggle"
            onClick={() => setSpreadMode(!spreadMode)}
            style={iconBtn(S, spreadMode)}
            onMouseEnter={(e) => { if (!spreadMode) { e.currentTarget.style.background = S.hover; e.currentTarget.style.borderColor = S.accent; } }}
            onMouseLeave={(e) => { if (!spreadMode) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = S.panelBorder; } }}
          >
            📖 <span className="tb-btn-text">{spreadMode ? "Book" : "Single"}</span>
          </button>

          <Divider S={S} />

          {/* Zoom control */}
          <div style={{ display: "flex", alignItems: "center", background: S.bg, border: `1px solid ${S.panelBorder}`, borderRadius: "8px", overflow: "hidden" }}>
            {[["−", zoomOut, "reader-zoom-out"], [`${Math.round(zoom * 100)}%`, zoomReset, null], ["+", zoomIn, "reader-zoom-in"]].map(([label, fn, id], idx) => (
              <button key={idx} id={id} onClick={fn} style={{
                background: "transparent", border: "none", color: S.text, cursor: "pointer",
                width: idx === 1 ? "46px" : "28px", height: "34px", fontSize: idx === 1 ? "0.7rem" : "0.95rem",
                fontWeight: 700, fontFamily: idx === 1 ? "'Outfit', sans-serif" : "monospace",
                display: "flex", alignItems: "center", justifyContent: "center",
                borderLeft: idx > 0 ? `1px solid ${S.panelBorder}` : "none",
                transition: "background 0.15s",
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = S.hover}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >{label}</button>
            ))}
          </div>

          {/* Theme toggle pill */}
          <div style={{ display: "flex", background: S.bg, border: `1px solid ${S.panelBorder}`, borderRadius: "8px", overflow: "hidden", height: "34px" }}>
            {[{ key: "light", icon: "☀" }, { key: "dark", icon: "🌙" }].map(({ key, icon }) => (
              <button key={key} id={`reader-theme-${key}`} onClick={() => changeTheme(key)}
                style={{
                  width: "34px", height: "100%", border: "none",
                  background: theme === key ? S.accent : "transparent",
                  color: theme === key ? "#fff" : S.text,
                  borderLeft: key !== "light" ? `1px solid ${S.panelBorder}` : "none",
                  fontSize: "0.82rem", cursor: "pointer", transition: "all 0.18s",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >{icon}</button>
            ))}
          </div>
        </div>

        {/* ── MOBILE RIGHT ── */}
        <div className="reader-toolbar-mobile" style={{ display: "none", alignItems: "center", gap: "6px", marginLeft: "auto", flexShrink: 0 }}>
          {!loadingPdf && !loadError && (
            <button
              onClick={() => { if (showAudioPlayer) { stopSpeech(true); setShowAudioPlayer(false); } else { setShowAudioPlayer(true); if (!playDisabled) setTimeout(() => startSpeech(), 300); } }}
              style={{ ...iconBtn(S, showAudioPlayer), width: "34px", padding: 0 }}
            >{isSpeaking ? "⏸" : "🔊"}</button>
          )}
          <button
            onClick={() => setShowSettings(!showSettings)}
            style={{ ...iconBtn(S, showSettings), width: "34px", padding: 0 }}
          >⚙️</button>
        </div>
      </div>

      {/* ── MOBILE PAGE NAV ROW ── */}
      <div className="reader-toolbar-mobile" style={{ display: "none", alignItems: "center", justifyContent: "center", gap: "8px", padding: "0 1rem 10px", borderTop: `1px solid ${S.panelBorder}` }}>
        {!loadingPdf && !loadError && (
          <>
            <button onClick={goPrev} disabled={pageNum <= 1} style={{ ...navBtn(S, pageNum <= 1), width: "36px", height: "36px" }}>‹</button>
            <span style={{ fontSize: "0.85rem", fontWeight: 700, color: S.text, fontFamily: "'Outfit', sans-serif" }}>{pageNum} / {numPages}</span>
            <button onClick={goNext} disabled={pageNum >= numPages} style={{ ...navBtn(S, pageNum >= numPages), width: "36px", height: "36px" }}>›</button>
          </>
        )}
      </div>

      {/* ── MOBILE SETTINGS PANEL ── */}
      {showSettings && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: "1rem",
          background: S.panelBg, border: `1px solid ${S.panelBorder}`,
          borderRadius: "14px", boxShadow: `0 12px 32px ${S.shadow}`,
          padding: "1.25rem", width: "280px", zIndex: 50,
          display: "flex", flexDirection: "column", gap: "1.1rem",
          animation: "scaleIn 0.15s ease both", transformOrigin: "top right",
        }}>
          <Label S={S}>Theme</Label>
          <div style={{ display: "flex", background: S.bg, border: `1px solid ${S.panelBorder}`, borderRadius: "8px", overflow: "hidden" }}>
            {[{ key: "light", label: "☀ Light" }, { key: "dark", label: "🌙 Dark" }].map(({ key, label }) => (
              <button key={key} onClick={() => changeTheme(key)} style={{ flex: 1, padding: "7px", border: "none", borderLeft: key !== "light" ? `1px solid ${S.panelBorder}` : "none", background: theme === key ? S.accent : "transparent", color: theme === key ? "#fff" : S.text, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "all 0.15s" }}>{label}</button>
            ))}
          </div>

          <Label S={S}>Zoom</Label>
          <div style={{ display: "flex", alignItems: "center", background: S.bg, border: `1px solid ${S.panelBorder}`, borderRadius: "8px", overflow: "hidden", alignSelf: "flex-start" }}>
            {[["−", zoomOut], [`${Math.round(zoom * 100)}%`, zoomReset], ["+", zoomIn]].map(([label, fn], i) => (
              <button key={i} onClick={fn} style={{ background: "transparent", border: "none", borderLeft: i > 0 ? `1px solid ${S.panelBorder}` : "none", color: S.text, cursor: "pointer", width: i === 1 ? "52px" : "32px", height: "32px", fontSize: i === 1 ? "0.72rem" : "1rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: i === 1 ? "'Outfit', sans-serif" : "monospace" }}>{label}</button>
            ))}
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => setSpreadMode(!spreadMode)} style={{ ...iconBtn(S, spreadMode), flex: 1, justifyContent: "center" }}>📖 Book View</button>
            <button onClick={() => { setShowNotesPanel((v) => !v); setShowSettings(false); }} style={{ ...iconBtn(S, showNotesPanel), flex: 1, justifyContent: "center" }}>✎ Notes{annotationCount > 0 ? ` (${annotationCount})` : ""}</button>
          </div>

          {showAudioPlayer && (bestFemale || bestMale) && (
            <>
              <div style={{ height: "1px", background: S.panelBorder }} />
              <Label S={S}>Voice</Label>
              <div style={{ display: "flex", gap: "6px" }}>
                {[[bestFemale, femaleActive, "Female"], [bestMale, maleActive, "Male"]].map(([v, active, label]) =>
                  v ? <button key={label} onClick={() => { setSelectedVoiceName(v.name); setVoiceGender(label.toLowerCase()); }} style={{ ...iconBtn(S, active), flex: 1, justifyContent: "center" }}>{label}</button> : null
                )}
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes waveBar0 { 0%,100% { height:3px } 50% { height:13px } }
        @keyframes waveBar1 { 0%,100% { height:7px } 50% { height:17px } }
        @keyframes waveBar2 { 0%,100% { height:3px } 50% { height:10px } }
        @keyframes scaleIn  { from { opacity:0; transform:scale(0.92) } to { opacity:1; transform:scale(1) } }
        @keyframes fadeUp   { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
        @media (max-width: 768px) {
          .reader-toolbar-desktop { display: none !important; }
          .reader-toolbar-mobile  { display: flex !important; }
        }
        @media (max-width: 1100px) { .tb-btn-text { display: none !important; } }
        @media (max-width: 820px)  { .tb-title, .tb-author { max-width: 120px !important; } }
      `}</style>
    </header>
  );
}

function Label({ S, children }) {
  return <span style={{ fontSize: "0.72rem", fontWeight: 700, color: S.text, opacity: 0.55, textTransform: "uppercase", letterSpacing: "0.08em" }}>{children}</span>;
}
